import { all, AllEffect, ForkEffect, put, call, takeEvery, take, select } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import BffApiService from '@/shared/bffApiService';
import {
  BonusesActionType,
  getBonuses,
  getBonusesError,
  getBonusesSuccess,
  GetBonusesSuccessAction,
  GetBonusesErrorAction,
  ShowPushNotificationBonusAction,
} from '@/store/bonuses/actions';
import { selectBtagPrefix } from '@/store/selectors/commonSelectors';
import { checkDevice } from '@/shared/utility';
import { modalOpen } from '@/store/modal/actions';
import { MODALS } from '@/features/modal/types';
import { BffBonusType } from '@lucky7ventures/bff-types';

export function* getBonusesSaga(): SagaIterator {
  const btag = yield select(selectBtagPrefix);
  const deviceTypeId = checkDevice();

  try {
    const response = yield call(BffApiService.fetchBonuses, { payload: { btag, deviceTypeId } });

    if (response.data.data) {
      yield put(getBonusesSuccess(response.data.data));
    }
  } catch (error) {
    yield put(getBonusesError());
  }
}

/**
 * Saga to handle push notification bonus display.
 * Waits for bonuses to be fetched, finds the specific bonus by ID,
 * and opens the modal only if the bonus is found.
 */
export function* showPushNotificationBonusSaga(
  action: ShowPushNotificationBonusAction,
): SagaIterator {
  const { bonusId } = action;

  // Step 1: Trigger bonus fetch
  yield put(getBonuses());

  // Step 2: Wait for fetch to complete (success or error)
  const result: GetBonusesSuccessAction | GetBonusesErrorAction = yield take([
    BonusesActionType.GetBonusesSuccess,
    BonusesActionType.GetBonusesError,
  ]);

  // Step 3: Handle error case - don't show modal
  if (result.type === BonusesActionType.GetBonusesError) {
    return;
  }

  // Step 4: Explicitly update bonuses in Redux state
  // This ensures Fluid wrapper and other components see the fresh bonus data
  yield put(getBonusesSuccess(result.bonuses));

  // Step 5: Find the specific bonus by ID
  const bonuses = result.bonuses || {};
  const availableBonuses = bonuses[BffBonusType.Available] || [];
  const targetBonus = availableBonuses.find(
    bonus => bonus.gigBonusId?.toString() === bonusId || bonus.slug === bonusId,
  );

  // Step 6: Handle not found case - don't show modal
  if (!targetBonus) {
    return;
  }

  // Step 7: Success - open modal with the specific bonus
  yield put(
    modalOpen(MODALS.AVAILABLE_BONUSES, {
      bonuses: [targetBonus],
    }),
  );
}

export function* watchBonusesSagas(): Generator<AllEffect<ForkEffect<never>>, void> {
  yield all([
    takeEvery(BonusesActionType.GetBonuses, getBonusesSaga),
    takeEvery(BonusesActionType.ShowPushNotificationBonus, showPushNotificationBonusSaga),
  ]);
}
